import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import useSystemTemplatePreviewMutation from 'components/domain/automation/systemTemplates/useSystemTemplatePreviewMutation';
import CheckboxField from 'components/forms/checkboxField/CheckboxField';
import DisplayField from 'components/forms/displayField/DisplayField';
import RichTextEditorField from 'components/forms/richTextEditor/RichTextEditorField';
import TextField from 'components/forms/textField/TextField';
import { LanguageCode } from 'i18n/internationalization.types';
import { SystemTemplate } from 'models/automation/SystemTemplates';
import { UsageType } from 'models/automation/Common';
import TemplateModalLanguageSelectField from 'pages/automations/common/TemplateModalLanguageSelectField';
import { SystemTemplateEditModalFormValues } from './SystemTemplateEditModal.schema';
import useSystemTemplateToolbarSettings from './fields/useSystemTemplateToolbarSettings';

interface SystemTemplateEditModalFormElementsProps {
  availableLanguages: LanguageCode[];
  systemTemplate: SystemTemplate;
}

const SystemTemplateEditModalFormElements = ({
  availableLanguages,
  systemTemplate,
}: SystemTemplateEditModalFormElementsProps) => {
  const { t } = useTranslation();
  const [isOnPreviewMode, setIsOnPreviewMode] = useState(false);
  const { control } = useFormContext<SystemTemplateEditModalFormValues>();
  const currentSelectedLanguage = useWatch({ control, name: 'language' });

  const {
    data: previewData,
    mutateAsync: fetchPreview,
    isLoading: isLoadingPreview,
  } = useSystemTemplatePreviewMutation();

  const { toolbarSettings, isLoadingVariables } =
    useSystemTemplateToolbarSettings({
      key: systemTemplate.key,
      loadPreview: async (content) => {
        const { data } = await fetchPreview({ htmlContent: content });

        return data.previewContent;
      },
      onEnterPreviewMode: () => setIsOnPreviewMode(true),
      onExitPreviewMode: () => setIsOnPreviewMode(false),
    });

  const { key } = systemTemplate;

  return (
    <>
      <DisplayField
        colSmInput={8}
        colSmLabel={4}
        colXsInput={8}
        colXsLabel={4}
        id="templateName"
        label={t('pageAutomations.systemTemplates.modal.templateName')}
        value={t('pageAutomations.systemTemplates.systemTemplateName', {
          context: key,
        })}
      />
      <DisplayField
        colSmInput={8}
        colSmLabel={4}
        colXsInput={8}
        colXsLabel={4}
        id="templateDescription"
        label={t('pageAutomations.systemTemplates.modal.templateDescription')}
        value={t('pageAutomations.systemTemplates.systemTemplateDescription', {
          context: key,
        })}
      />

      <TemplateModalLanguageSelectField usageType={UsageType.SYSTEM} />

      {isOnPreviewMode && (
        <TextField
          colSmInput={8}
          colSmLabel={4}
          disabled={isOnPreviewMode}
          isLoading={isLoadingPreview}
          label={t('common.subject')}
          name="subjectPreview"
          value={
            previewData?.data.previewTitle ??
            t('form.editor.preview.errorMessage')
          }
        />
      )}

      {availableLanguages.map(
        (language) =>
          language &&
          currentSelectedLanguage === language && (
            <>
              {!isOnPreviewMode && (
                <TextField
                  colSmInput={8}
                  colSmLabel={4}
                  disabled={isOnPreviewMode}
                  label={t('common.subject')}
                  name={`locales.${currentSelectedLanguage}.subject`}
                />
              )}

              <RichTextEditorField
                colSmInput={12}
                colSmLabel={0}
                isLoading={isLoadingVariables}
                linkMode="edit"
                name={`locales.${currentSelectedLanguage}.content`}
                toolbarSettings={toolbarSettings}
              />
            </>
          ),
      )}

      <CheckboxField name="sendCopyToAgency">
        {t('pageAutomations.systemTemplates.modal.sendCopyToAgency')}
      </CheckboxField>
    </>
  );
};

export default SystemTemplateEditModalFormElements;
